/**
 * CopyConnectorModal - Copy connectors to another project
 */
import React, { useState, useEffect } from 'react';
import { Modal, Select, Alert, List, Tag, Button } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { listProjects, createConnector } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';

const { Option } = Select;

function CopyConnectorModal({ open, onClose, connectors, currentProjectId, onSuccess }) {
  const [projects, setProjects] = useState([]);
  const [targetProjectId, setTargetProjectId] = useState(null);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (open) {
      fetchProjects();
    }
  }, [open]);

  const fetchProjects = async () => {
    try {
      const res = await listProjects();
      // Filter out current project
      const availableProjects = (res.data.projects || []).filter(p => p.id !== currentProjectId);
      setProjects(availableProjects);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      showError(error);
    }
  };

  const handleCopy = async () => {
    if (!targetProjectId) {
      showError('Please select a target project');
      return;
    }

    setCopying(true);
    try {
      // Copy each connector to the target project
      for (const connector of connectors) {
        await createConnector(targetProjectId, {
          name: `${connector.name} (Copy)`,
          connector_type: connector.connector_type,
          config: connector.config || {}
        });
      }

      showSuccess(`Successfully copied ${connectors.length} connector${connectors.length > 1 ? 's' : ''} to ${getTargetProjectName()}`);
      if (onSuccess) onSuccess();
      onClose();
      setTargetProjectId(null);
    } catch (error) {
      console.error('Failed to copy connectors:', error);
      showError(error);
    } finally {
      setCopying(false);
    }
  };

  const getTargetProjectName = () => {
    const project = projects.find(p => p.id === targetProjectId);
    return project ? project.name : '';
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CopyOutlined />
          <span>Copy Connector{connectors.length > 1 ? 's' : ''}</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={copying}>
          Cancel
        </Button>,
        <Button
          key="copy"
          type="primary"
          onClick={handleCopy}
          disabled={copying || !targetProjectId}
          loading={copying}
        >
          {copying ? 'Copying...' : `Copy ${connectors.length} Connector${connectors.length > 1 ? 's' : ''}`}
        </Button>
      ]}
      width={600}
    >
      <div style={{ marginBottom: '16px' }}>
        <Alert
          message={`You are about to copy ${connectors.length} connector${connectors.length > 1 ? 's' : ''} to another project`}
          type="info"
          showIcon
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Connectors to Copy:
        </label>
        <List
          size="small"
          bordered
          dataSource={connectors}
          renderItem={(conn) => (
            <List.Item>
              <span><strong>{conn.name}</strong></span>
              <Tag color="blue">{conn.connector_type}</Tag>
            </List.Item>
          )}
        />
      </div>

      <div>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Target Project: <span style={{ color: 'red' }}>*</span>
        </label>
        <Select
          style={{ width: '100%' }}
          placeholder="Select target project"
          value={targetProjectId}
          onChange={setTargetProjectId}
          showSearch
          optionFilterProp="children"
        >
          {projects.map(p => (
            <Option key={p.id} value={p.id}>{p.name}</Option>
          ))}
        </Select>
        {projects.length === 0 && (
          <p style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
            No other projects available
          </p>
        )}
      </div>
    </Modal>
  );
}

export default CopyConnectorModal;
